// Home / `/` landing — the static-site replacement for what would have been
// "your boards" once the backend lands. Pulls recent board IDs from
// localStorage (rememberRecentBoard is called from BoardPage on mount) and
// lets the user open one or mint a brand-new id.
//
// This page is intentionally lightweight: no auth, no fetch, just a list +
// two buttons. Production "list my boards (from server)" lives here when the
// backend returns.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/Button';
import { forgetRecentBoard, listRecentBoards, type RecentBoard } from './recentBoards';
import { deleteLocalBoard } from '../sync/localPersistence';
import { TemplatePicker, makeBoardId } from '../templates/components/TemplatePicker';

export function HomePage() {
  const [boards, setBoards] = useState<RecentBoard[]>([]);
  // The picker opens on "New board" and closes on Esc / outside-click /
  // template-selected (navigates away). pickerTick re-mounts the picker
  // after the user saves/deletes a user template inside it, so the list
  // updates without a page refresh.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTick, setPickerTick] = useState(0);

  useEffect(() => {
    setBoards(listRecentBoards());
  }, []);

  const removeBoard = useCallback(async (id: string) => {
    forgetRecentBoard(id);
    await deleteLocalBoard(id);
    setBoards(listRecentBoards());
  }, []);

  return (
    <div className="absolute inset-0 overflow-y-auto bg-bg-canvas">
      <div className="max-w-[720px] mx-auto px-8 py-12">
        <h1 className="text-24 font-semibold text-fg-primary m-0">Boards</h1>
        <p className="text-14 text-fg-secondary leading-[1.5] max-w-[60ch] mt-2">
          Each board is saved locally in your browser. Share a live invite link from inside a
          board to collaborate.
        </p>

        <div className="mt-6">
          <Button variant="primary" size="md" onClick={() => setPickerOpen(true)}>
            <Plus size={14} strokeWidth={1.5} />
            New board
          </Button>
        </div>

        <h2 className="font-mono text-12 font-medium uppercase tracking-[0.02em] text-fg-tertiary mt-8 mb-3">
          Recent
        </h2>
        {boards.length === 0 ? (
          <p className="text-13 text-fg-tertiary">
            No saved boards yet. Hit “New board” to start one.
          </p>
        ) : (
          <ul className="m-0 p-0 list-none flex flex-col gap-1.5">
            {boards.map((b) => (
              <RecentRow key={b.id} board={b} onRemove={() => removeBoard(b.id)} />
            ))}
          </ul>
        )}
      </div>
      {pickerOpen && (
        <TemplatePicker
          key={pickerTick}
          onClose={() => setPickerOpen(false)}
          newBoardId={makeBoardId}
          onRefresh={() => setPickerTick((t) => t + 1)}
        />
      )}
    </div>
  );
}

function RecentRow({ board, onRemove }: { board: RecentBoard; onRemove: () => void }) {
  const navigate = useNavigate();
  return (
    <li
      className="
        bg-bg-panel border border-border-default rounded-2
        flex items-center gap-3 px-3 py-2
        hover:bg-bg-hover transition-colors duration-micro ease-out
      "
    >
      <button
        type="button"
        onClick={() => navigate(`/b/${board.id}`)}
        className="
          flex-1 min-w-0 text-left
          inline-flex items-center justify-between gap-3
          focus-visible:outline-none
        "
      >
        <span className="text-13 text-fg-primary truncate font-medium">{board.id}</span>
        <span className="font-mono text-12 text-fg-tertiary tabular-nums shrink-0">
          {relativeTime(board.lastOpened)}
        </span>
      </button>
      <button
        type="button"
        onClick={onRemove}
        title="Forget this board (also clears its local data)"
        aria-label={`Forget ${board.id}`}
        className="w-7 h-7 inline-flex items-center justify-center rounded-2 text-fg-tertiary hover:bg-error-subtle hover:text-error"
      >
        <Trash2 size={14} strokeWidth={1.5} />
      </button>
    </li>
  );
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

// Short URL-safe board id generation moved into the templates module as
// `makeBoardId` so the picker and home page share one source. See
// apps/web/src/templates/components/TemplatePicker.ts.
